/**
 * Training Split Analysis — categorizes exercises into movement patterns
 * (Push / Pull / Legs / Core / Carry) and returns percentage breakdown
 * over a given time window.
 */

import { supabase } from './supabase';

export type MovementPattern = 'Push' | 'Pull' | 'Upper' | 'Lower' | 'Core' | 'Carry' | 'Other';

export interface TrainingSplitResult {
  pattern: MovementPattern;
  count: number;
  percentage: number;
}

/**
 * Mapping from exercise category / body_part / name keywords → movement pattern.
 * Uses a hierarchical approach: name keywords first, then category, then body_part.
 */
const NAME_KEYWORDS: Record<string, MovementPattern> = {
  // Push
  'bench': 'Push',
  'push-up': 'Push',
  'push up': 'Push',
  'dip': 'Push',
  'fly': 'Push',
  'pushdown': 'Push',
  'chest': 'Push',
  // Pull
  'pull-up': 'Pull',
  'pull up': 'Pull',
  'pullup': 'Pull',
  'chin-up': 'Pull',
  'chin up': 'Pull',
  'row': 'Pull',
  'pulldown': 'Pull',
  'reverse fly': 'Pull',
  'deadlift': 'Lower',
  'rack pull': 'Lower',
  // Upper (horizontal/vertical push+pull that don't fit pure Push/Pull)
  'overhead': 'Upper',
  'press': 'Upper',
  'curl': 'Upper',
  'tricep': 'Upper',
  'lateral raise': 'Upper',
  'face pull': 'Upper',
  'shrug': 'Upper',
  'rear delt': 'Upper',
  // Lower
  'squat': 'Lower',
  'leg press': 'Lower',
  'lunge': 'Lower',
  'calf': 'Lower',
  'hamstring': 'Lower',
  'leg curl': 'Lower',
  'leg extension': 'Lower',
  'hip thrust': 'Lower',
  'glute': 'Lower',
  'rdl': 'Lower',
  'romanian': 'Lower',
  'hack squat': 'Lower',
  'pistol': 'Lower',
  // Core
  'plank': 'Core',
  'crunch': 'Core',
  'ab ': 'Core',
  'rollout': 'Core',
  'russian twist': 'Core',
  'pallof': 'Core',
  'dead bug': 'Core',
  'hanging leg raise': 'Core',
  // Carry
  'carry': 'Carry',
  'farmer': 'Carry',
  'suitcase': 'Carry',
  'yoke': 'Carry',
};

const CATEGORY_MAP: Record<string, MovementPattern> = {
  chest: 'Push',
  shoulders: 'Push',
  back: 'Pull',
  arms: 'Upper', // biceps/triceps both Upper
  legs: 'Lower',
  core: 'Core',
  full_body: 'Carry',
  calisthenics: 'Other',
  cardio: 'Other',
};

function classifyExercise(name: string, category?: string, bodyPart?: string): MovementPattern {
  const lower = name.toLowerCase();
  for (const [keyword, pattern] of Object.entries(NAME_KEYWORDS)) {
    if (lower.includes(keyword)) return pattern;
  }
  if (category && CATEGORY_MAP[category]) return CATEGORY_MAP[category];
  if (bodyPart && CATEGORY_MAP[bodyPart]) return CATEGORY_MAP[bodyPart];
  return 'Other'; // fallback
}

/**
 * Fetch workout data for the last `days` days and return movement pattern breakdown.
 */
export async function getTrainingSplit(
  userId: string,
  days: number = 30
): Promise<TrainingSplitResult[]> {
  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data: workouts } = await supabase
    .from('workouts')
    .select('payload')
    .eq('user_id', userId)
    .gte('performed_at', since.toISOString())
    .order('performed_at', { ascending: false });

  if (!workouts || workouts.length === 0) return [];

  const counts: Record<MovementPattern, number> = {
    Push: 0, Pull: 0, Upper: 0, Lower: 0, Core: 0, Carry: 0, Other: 0,
  };

  let total = 0;
  for (const w of workouts) {
    const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
    const exercises = payload?.exercises ?? [];
    for (const ex of exercises) {
      const setCount = (ex.sets?.length ?? 0) || 1;
      const pattern = classifyExercise(
        ex.name ?? ex.exercise_name ?? '',
        ex.category,
        ex.body_part
      );
      counts[pattern] += setCount;
      total += setCount;
    }
  }

  if (total === 0) return [];

  const ALL_PATTERNS: MovementPattern[] = ['Push', 'Pull', 'Upper', 'Lower', 'Core', 'Carry', 'Other'];
  return ALL_PATTERNS
    .map(pattern => ({
      pattern,
      count: counts[pattern],
      percentage: Math.round((counts[pattern] / total) * 100),
    }))
    .sort((a, b) => b.percentage - a.percentage);
}
